//#region IMPORTS
import {
  InstoreOrderLine,
  InstoreOrderLineTableRow,
} from "../models/instoreOrder";
import { useEffect, useState, useCallback } from "react";
import { instoreOrderLinesSelectors } from "../features/instoreOrders/instoreOrdersSlice";
import { useAppSelector } from "../store/configureStore";
import { InstoreTheme } from "../models/instoreTheme";
import { StoreFormat } from "../models/storeFormat";
//#endregion

export function useLocalInstoreOrderLines(storeFormat?: StoreFormat) {
  //#region REDUX STATE
  const reduxRows = useAppSelector(instoreOrderLinesSelectors.selectAll);
  const { instoreOrderLinesLoaded } = useAppSelector(
    ({ instoreOrders }) => instoreOrders
  );
  //#endregion

  //#region LOCAL STATE
  const [localTableData, setLocalTableData] = useState<
    InstoreOrderLineTableRow[]
  >([]);
  const [isDirty, setIsDirty] = useState(false);
  //#endregion

  //#region HELPERS
  const recalcFlatIndexes = useCallback((rows: InstoreOrderLineTableRow[]) => {
    let flatIndex = 0;
    const assign = (row?: InstoreOrderLineTableRow) => {
      if (!row) return;
      row.flatIndex = flatIndex++;
      row.children?.forEach(assign);
    };
    rows.forEach(assign);
  }, []);
  //#endregion

  //#region TABLE DATA BUILDER
  const buildTableData = useCallback(
    (rows: InstoreOrderLine[]): InstoreOrderLineTableRow[] => {
      if (!rows) return [];

      const rowsToRender: InstoreOrderLineTableRow[] = [];
      const themes = new Map<string | number, InstoreOrderLine[]>();

      for (const item of rows) {
        const key =
          item.theme_id ?? item.theme_name ?? `theme_${String(item.id)}`;
        if (!themes.has(key)) themes.set(key, []);
        themes.get(key)!.push(item);
      }

      for (const items of themes.values()) {
        if (!items.length) continue;
        const themeInfo = items[0];

        // Map all items under this theme as children
        const children: InstoreOrderLineTableRow[] = items.map(
          (item, index) => ({
            rowKind: "article",
            db_ids: [item.id],
            index,
            theme_name: item.theme_name,
            theme_id: item.theme_id,
            article_name: item.article_name,
            custom_fields: item.custom_fields,
            store_format_name: item.store_format_name,
            store_format_id: item.store_format_id,
            amount: item.amount,
            parent: false,
          })
        );

        rowsToRender.push({
          rowKind: "theme",
          db_ids: items.map((i) => i.id),
          index: 0,
          theme_name: themeInfo.theme_name,
          theme_id: themeInfo.theme_id,
          article_name: themeInfo.theme_name,
          amount: 0,
          parent: true,
          children: children.sort((a, b) => (a.index ?? 0) - (b.index ?? 0)),
          store_format_name: themeInfo.store_format_name,
          store_format_id: themeInfo.store_format_id,
        });
      }

      recalcFlatIndexes(rowsToRender);
      return rowsToRender;
    },
    [recalcFlatIndexes]
  );
  //#endregion

  //#region ADD THEME ROW
  const addThemeRow = useCallback(
    (theme: InstoreTheme) => {
      setLocalTableData((prev) => {
        const next = [...prev];
        const parent: InstoreOrderLineTableRow = {
          rowKind: "theme",
          parent: true,
          db_ids: [],
          theme_name: theme.name,
          theme_id: theme.id,
          article_name: theme.name,
          index: 0,
          children: [],
          store_format_name: "",
          store_format_id: 0,
          amount: 0,
        };
        const child: InstoreOrderLineTableRow = {
          rowKind: "article",
          parent: false,
          db_ids: [],
          theme_name: theme.name,
          theme_id: theme.id,
          article_name: "enter name",
          index: 0,
          custom_fields: [],
          placeholder: true,
          store_format_name: "",
          store_format_id: 0,
          amount: 0,
        };
        parent.children!.push(child);
        next.push(parent);

        recalcFlatIndexes(next);
        setIsDirty(true);
        return next;
      });
    },
    [recalcFlatIndexes]
  );
  //#endregion

  //#region ADD ARTICLE ROW
  const addArticleRow = useCallback(
    (themeId: number) => {
      storeFormat &&
        setLocalTableData((prev) => {
          const next = [...prev];
          const parent = next.find((r) => r.theme_id === themeId && r.parent);
          if (!parent) return prev;

          const newArticle: InstoreOrderLineTableRow = {
            rowKind: "article",
            parent: false,
            db_ids: [],
            theme_name: parent.theme_name,
            theme_id: parent.theme_id,
            article_name: "enter a name",
            index: 0,
            custom_fields: [],
            placeholder: true,
            store_format_name: storeFormat.name,
            store_format_id: storeFormat.id,
            amount: 0,
          };

          parent.children = parent.children
            ? [...parent.children, newArticle]
            : [newArticle];

          recalcFlatIndexes(next);
          setIsDirty(true);
          return next;
        });
    },
    [storeFormat, recalcFlatIndexes]
  );
  //#endregion

  //#region MOVE ROW ANYWHERE
  const moveRowAnywhere = useCallback(
    (fromFlatIndex: number, toFlatIndex: number) => {
      setLocalTableData((prev) => {
        const allRows: InstoreOrderLineTableRow[] = [];
        prev.forEach((parent) => {
          allRows.push(parent);
          parent.children?.forEach((child) => allRows.push(child));
        });

        if (
          fromFlatIndex < 0 ||
          fromFlatIndex >= allRows.length ||
          toFlatIndex < 0 ||
          toFlatIndex >= allRows.length
        ) {
          console.warn("Invalid flatIndex for moveRowAnywhere", {
            fromFlatIndex,
            toFlatIndex,
            totalRows: allRows.length,
          });
          return prev;
        }

        const movingRow = allRows[fromFlatIndex];
        const movedRows = movingRow.parent
          ? [movingRow, ...(movingRow.children ?? [])]
          : [movingRow];

        allRows.splice(fromFlatIndex, movedRows.length);
        allRows.splice(toFlatIndex, 0, ...movedRows);

        const newParents: InstoreOrderLineTableRow[] = [];
        let currentParent: InstoreOrderLineTableRow | null = null;

        for (const row of allRows) {
          if (row.parent) {
            currentParent = { ...row, children: [] };
            newParents.push(currentParent);
          } else {
            if (!currentParent) continue;
            row.theme_name = currentParent.theme_name;
            row.theme_id = currentParent.theme_id;
            currentParent.children!.push(row);
          }
        }

        const cleanedParents = newParents.filter(
          (p) => !p.parent || (p.children && p.children.length > 0)
        );

        recalcFlatIndexes(cleanedParents);
        setIsDirty(true);
        return cleanedParents;
      });
    },
    [recalcFlatIndexes]
  );
  //#endregion

  //#region UPDATE ROW CELLS LOCALLY
  const updateRowCells = useCallback(
    (columnId: string, value: any, row: InstoreOrderLineTableRow) => {
      setLocalTableData((prev) => {
        let updated = false;

        const update = (
          rows: InstoreOrderLineTableRow[]
        ): InstoreOrderLineTableRow[] =>
          rows.map((r) => {
            if (r === row) {
              updated = true;
              return { ...r, [columnId]: value, placeholder: false };
            }

            if (r.children) {
              return { ...r, children: update(r.children) };
            }

            return r;
          });

        const next = update(prev);
        if (updated) setIsDirty(true);
        return next;
      });
    },
    []
  );
  //#endregion

  //#region DELETE ROW LOCALLY
  const removeRow = useCallback(
    (row: InstoreOrderLineTableRow) => {
      setLocalTableData((prev) => {
        const filterRows = (
          rows: InstoreOrderLineTableRow[]
        ): InstoreOrderLineTableRow[] =>
          rows
            .filter((r) => r !== row)
            .map((r) => {
              if (r.children) r.children = filterRows(r.children);
              return r;
            })
            .filter((r) =>
              r.parent ? r.children && r.children.length > 0 : true
            ); // remove parent with no children

        const newData = filterRows(prev);
        recalcFlatIndexes(newData);
        setIsDirty(true);
        return newData;
      });
    },
    [recalcFlatIndexes]
  );
  //#endregion

  //#region INITIALIZE LOCAL STATE
  useEffect(() => {
    if (!instoreOrderLinesLoaded) {
      setLocalTableData([]);
    }
    
    if (!reduxRows || reduxRows.length === 0) return;

    setLocalTableData(
      buildTableData(reduxRows.sort((a, b) => (a.index ?? 0) - (b.index ?? 0)))
    );
  }, [instoreOrderLinesLoaded, reduxRows, buildTableData]);
  //#endregion

  //#region RETURNED API
  return {
    setLocalTableData,
    moveRowAnywhere,
    localTableData,
    updateRowCells,
    addArticleRow,
    addThemeRow,
    removeRow,
    isDirty,
  };
  //#endregion
}
